<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, onMounted, watch, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import _ from 'lodash'
import Multiselect from 'vue-multiselect'
import IntegrationService from '@/services/IntegrationService'
import FormSwitch from '../../components/Atoms/FormSwitch.vue'
import FormButton from '../../components/Atoms/FormButton.vue'
import FormInput from '../../components/Atoms/FormInput.vue'
import FormText from '../../components/Atoms/FormText.vue'
import Spinner from '../../components/ui/Spinner.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import Badge from '../../components/Badge.vue'
import IconInfo from '../icon/IconInfo.vue'
import IconBase from '../icon/IconBase.vue'
import IconCheckmark from '../icon/IconCheckmark.vue'
import Collapse from '../Atoms/Collapse.vue'
import IconEye from '../icon/IconEye.vue'
import IconEyeCrossed from '../icon/IconEyeCrossed.vue'

const SOCIAL_KEYS = new Set([
	'socialSharingEnabled',
	'shareFacebook',
	'shareTwitter',
	'shareLinkedIn',
	'shareReddit',
	'shareEmail',
	'shareWhatsApp',
])

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance()
const root = instance.proxy.$root

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		}
	}
})

const emit = defineEmits({
	'on-update': () => true
})

const countryBlockSelection = ref([])
const countryWhitelistSelection = ref([])
const countryList = ref([])
const countryListLoaded = ref(false)
const whitelistedDomains = ref([])
const whitelistedDomainsLoaded = ref(false)

const features = ref({
	countryBlock: {
		enabled: false,
		value: [],
		valueType: 'array'
	},
	countryWhitelists: {
		enabled: false,
		value: [],
		valueType: 'array'
	},
	domainWhitelists: {
		enabled: false,
		value: [],
		valueType: 'array'
	},
	embedPassword: {
		enabled: false,
		value: null,
		valueType: 'string'
	},
	socialSharingEnabled: {
		status: false,
	},
	shareFacebook: {
		status: true,
	},
	shareTwitter: {
		status: true,
	},
	shareLinkedIn: {
		status: false,
	},
	shareReddit: {
		status: false,
	},
	shareEmail: {
		status: false,
	},
	shareWhatsApp: {
		status: false,
	},
})

const featureProcessing = ref({
	countryBlock: false,
	countryWhitelists: false,
	domainWhitelists: false,
	embedPassword: false,
	socialSharingEnabled: false,
	shareFacebook: false,
	shareTwitter: false,
	shareLinkedIn: false,
	shareReddit: false,
	shareEmail: false,
	shareWhatsApp: false,
})

const featureDisabled = ref({
	countryBlock: true,
	countryWhitelists: true,
	domainWhitelists: true,
	embedPassword: true,
})

const temporaryNotification = ref(false)
const COLLAPSE_KEYS = ['embedPassword', 'countryBlock', 'countryWhitelists', 'domainWhitelists', 'socialSharing']

const collapseOpen = ref({
	embedPassword: false,
	countryBlock: false,
	countryWhitelists: false,
	domainWhitelists: false,
	socialSharing: false,
})

const isFeatureActive = (feature) => {
	if (feature === 'embedPassword') {
		return features.value.embedPassword.enabled && features.value.embedPassword.value?.length > 0
	}
	if (feature === 'countryBlock') {
		return features.value.countryBlock.enabled && features.value.countryBlock.value?.length > 0
	}
	if (feature === 'countryWhitelists') {
		return features.value.countryWhitelists.enabled && features.value.countryWhitelists.value?.length > 0
	}
	if (feature === 'domainWhitelists') {
		return features.value.domainWhitelists.enabled && features.value.domainWhitelists.value?.length > 0
	}
	if (feature === 'socialSharing') {
		return features.value.socialSharingEnabled.status
	}
	return false
}

const subscriptions = computed(() => store.getters['User/subscriptions'])
const liveSub = computed(() => subscriptions.value.find(sub => sub.category === 'live'))

const permissions = computed(() => {
	const domainWhiteList = !_.isNil(liveSub.value?.definitionOverride?.['playlist:domain-whitelist']) ? liveSub.value?.definitionOverride?.['playlist:domain-whitelist'] : (liveSub.value?.package?.definition?.['playlist:domain-whitelist'] || false)
	const geoBlock = !_.isNil(liveSub.value?.definitionOverride?.['playlist:geo-block']) ? liveSub.value?.definitionOverride?.['playlist:geo-block'] : (liveSub.value?.package?.definition?.['playlist:geo-block'] || false)
	const geoWhitelist = !_.isNil(liveSub.value?.definitionOverride?.['playlist:geo-whitelist']) ? liveSub.value?.definitionOverride?.['playlist:geo-whitelist'] : (liveSub.value?.package?.definition?.['playlist:geo-whitelist'] || false)

	return {
		countryBlock: geoBlock,
		countryWhitelists: geoWhitelist,
		domainWhitelists: domainWhiteList,
		embedPassword: true,
	}
})

const syncCollapseOpen = () => {
	COLLAPSE_KEYS.forEach((key) => {
		collapseOpen.value[key] = isFeatureActive(key)
	})
}

COLLAPSE_KEYS.forEach((key) => {
	watch(() => isFeatureActive(key), (active) => {
		if (active) collapseOpen.value[key] = true
	})
})

onMounted(async () => {
	syncFeatures()
	syncCollapseOpen()

	countryList.value = await IntegrationService.getCountryList()
	countryListLoaded.value = true

	countryBlockSelection.value = _.map(
		features.value.countryBlock.value,
		code => _.find(countryList.value, { code })
	)
	countryWhitelistSelection.value = _.map(
		features.value.countryWhitelists.value,
		code => _.find(countryList.value, { code })
	)

	whitelistedDomains.value = _.join(
		_.filter(features.value.domainWhitelists.value, item => item !== 'https://castr.com'),
		'\r\n',
	)
	whitelistedDomainsLoaded.value = true
})

watch(() => props.stream?._id, () => {
	syncFeatures()
	syncCollapseOpen()
})

const syncFeatures = () => {
	const settings = _.cloneDeep(props.stream?.settings)

	const defaultFeature = () => ({
		enabled: false,
		value: [],
		valueType: 'array',
	})

	const defaultSocialFeature = (status = false) => ({ status })

	features.value.countryBlock = settings?.countryBlock ?? defaultFeature()
	features.value.countryWhitelists = settings?.countryWhitelists ?? defaultFeature()
	features.value.domainWhitelists = settings?.domainWhitelists ?? defaultFeature()
	features.value.embedPassword = settings?.embedPassword ?? {
		enabled: false,
		value: null,
		valueType: 'string',
	}

	features.value.socialSharingEnabled = settings?.socialSharingEnabled ?? defaultSocialFeature()
	features.value.shareFacebook = settings?.shareFacebook ?? defaultSocialFeature(true)
	features.value.shareTwitter = settings?.shareTwitter ?? defaultSocialFeature(true)
	features.value.shareLinkedIn = settings?.shareLinkedIn ?? defaultSocialFeature()
	features.value.shareReddit = settings?.shareReddit ?? defaultSocialFeature()
	features.value.shareEmail = settings?.shareEmail ?? defaultSocialFeature()
	features.value.shareWhatsApp = settings?.shareWhatsApp ?? defaultSocialFeature()
}

const saveGeoBlocking = async () => {
	const updatedSettings = countryBlockSelection.value
	await saveSetting('countryBlock', _.map(updatedSettings, 'code'))
}

const saveGeoWhitelisting = async () => {
	const updatedSettings = countryWhitelistSelection.value
	await saveSetting(
		'countryWhitelists',
		_.map(updatedSettings, 'code')
	)
}

const saveWhitelistedDomains = async () => {
	let domains = _.split(whitelistedDomains.value, '\n')
		.map(d => d.trim())
		.filter(Boolean)
	const castrExist = domains.find((item) => item === 'https://castr.com')
	if (_.isEmpty(castrExist)) {
		domains = [...domains, 'https://castr.com']
	}
	if (!domains.length) return

	await saveSetting('domainWhitelists', domains)
}

const saveSetting = async (key, value) => {
	featureProcessing.value[key] = true
	try {
		if (SOCIAL_KEYS.has(key)) {
			features.value[key].status = value
		} else {
			features.value[key].value = value
		}
		emit('on-update', { key, value: features.value[key] })
	} catch (e) {
		notify({ group: 'error', text: 'Unable to save your changes.' })
	}
	featureProcessing.value[key] = false
	if (!SOCIAL_KEYS.has(key)) {
		featureDisabled.value[key] = true
	}
}

const isDisabled = (option) => {
	if (featureProcessing.value[option]) return true
	if (featureDisabled.value[option]) return true
	return false
}

const enableSave = (feature) => featureDisabled.value[feature] = false

const toggleFeature = async (featureName) => {
	if (featureProcessing.value[featureName]) return

	const feature = features.value[featureName]
	if (!feature) return

	let isToggledOn = features.value[featureName].enabled

	if (isToggledOn && !permissions.value[featureName]) {
		showModal('feature-upgrade')
		isToggledOn = false
		setTimeout(() => {
			features.value[featureName].enabled = !features.value[featureName].enabled
		}, 500)
	}

	let newValue = []
	if (isToggledOn) {
		newValue = features.value[featureName].value
	}
	await saveSetting(featureName, newValue)
}

const toggleSocialFeature = async (featureName) => {
	if (featureProcessing.value[featureName]) return
	const newValue = features.value[featureName].status
	await saveSetting(featureName, newValue)
}

const onSocialSharingToggle = async () => {
	await saveSetting('socialSharingEnabled', !features.value.socialSharingEnabled.status)
}

const setStreamPassword = async () => {
	const password = features.value.embedPassword.value
	if (!password) return
	await saveSetting('embedPassword', password)
	temporaryNotification.value = true
	setTimeout(() => {
		temporaryNotification.value = false
	}, 3000)
}

const passwordVisibility = ref(false)

const togglePasswordVisibility = () => {
	passwordVisibility.value = !passwordVisibility.value
}

</script>

<template>
	<aside class="bg-surface-2 !p-3 !rounded-lg !mb-4">
		<!-- Password Protection -->
		<Collapse class="!mb-4" v-model:is-open="collapseOpen.embedPassword">
			<template #header>
				<h4 class="text-tiny font-medium">Password Protection</h4>
				<Tooltip wrapper-class="block" content="Set password to access the player" v-show="!collapseOpen.embedPassword">
					<icon-base class="shrink-0 !ml-2">
						<icon-info />
					</icon-base>
				</Tooltip>
				<Badge class="ml-auto" pill v-if="!collapseOpen.embedPassword"
					:type="features.embedPassword.enabled ? 'enabled' : 'main'">{{ features.embedPassword.enabled ? '• Enabled' :
						'Disabled' }}</Badge>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2">
					<p class="text-xs text-surface-8">Require a password to access this player</p>
					<FormSwitch class="ml-auto !block" @change="toggleFeature('embedPassword')" v-model="features.embedPassword.enabled"
						:disabled="featureProcessing.embedPassword" />
				</div>
				<div class="flex items-end gap-x-2 !mt-1" v-if="features.embedPassword.enabled">
					<div class="relative flex items-center w-full">
						<FormInput label="Password" :type="passwordVisibility ? 'text' : 'password'" input-classes="!bg-surface-4"
							v-model="features.embedPassword.value" helper-class="top-10" @input="enableSave('embedPassword')"
							placeholder="Set Password" />
						<FormButton isIcon size="sm" type="link" class="absolute right-0 bottom-px" @click="togglePasswordVisibility">
							<icon-base class="shrink-0" fill="none">
								<icon-eye v-if="!passwordVisibility" />
								<icon-eye-crossed v-else />
							</icon-base>
						</FormButton>
					</div>
					<FormButton class="shrink-0" :disabled="isDisabled('embedPassword')" @click="setStreamPassword">
						{{ featureProcessing.embedPassword ? 'Saving' : 'Save' }}
					</FormButton>
				</div>
				<p v-if="temporaryNotification" class="text-xs text-success flex items-center gap-2 !mt-2">
					<icon-base class="shrink-0 !ml-2"><icon-checkmark /></icon-base>
					Password saved successfully
				</p>
			</template>
		</Collapse>
		<!-- Country Whitelist -->
		<Collapse class="!mb-4" v-model:is-open="collapseOpen.countryWhitelists">
			<template #header>
				<h4 class="text-tiny font-medium">Whitelist Countries</h4>
				<Tooltip wrapper-class="block" content="Player will only be accessible in selected countries"
					v-show="!collapseOpen.countryWhitelists">
					<icon-base class="shrink-0 !ml-2">
						<icon-info />
					</icon-base>
				</Tooltip>
				<Badge class="ml-auto" pill v-if="!collapseOpen.countryWhitelists"
					:type="features.countryWhitelists.enabled ? 'enabled' : 'main'">{{ features.countryWhitelists.enabled ? '• Enabled' : 'Disabled' }}</Badge>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2">
					<p class="text-xs text-surface-8">Player will only be accessible in selected countries</p>
					<Tooltip wrapper-class="block" :active="!permissions?.countryWhitelists">
						<FormSwitch class="!block" v-model="features.countryWhitelists.enabled"
							:disabled="featureProcessing.countryWhitelists || !permissions?.countryWhitelists"
							@change="toggleFeature('countryWhitelists')" />
						<template #content>
							Please <a class="underline" target="_blank" href="/app/subscribe">upgrade</a> subscription to use this
							feature.
						</template>
					</Tooltip>
				</div>
				<div class="col-span-2 flex items-center gap-x-2 !mt-1" v-if="features.countryWhitelists.enabled">
					<template v-if="countryListLoaded">
						<multiselect v-model="countryWhitelistSelection" :options="countryList" :multiple="true" :taggable="true"
							tag-placeholder="Unsupported region" placeholder="Search countries..." label="name" track-by="code"
							class="min-h-[2rem] !bg-surface-4" @select="enableSave('countryWhitelists')"
							@remove="enableSave('countryWhitelists')" />
						<FormButton class="shrink-0" :disabled="isDisabled('countryWhitelists')" @click="saveGeoWhitelisting">
							{{ featureProcessing.countryWhitelists ? 'Saving' : 'Save' }}
						</FormButton>
					</template>
					<template v-else>
						<Spinner />
					</template>
				</div>
			</template>
		</Collapse>
		<!-- Country Block -->
		<Collapse class="!mb-4" v-model:is-open="collapseOpen.countryBlock">
			<template #header>
				<h4 class="text-tiny font-medium">Blacklist Countries</h4>
				<Tooltip wrapper-class="block" content="Player will not be accessible in selected countries"
					v-show="!collapseOpen.countryBlock">
					<icon-base class="shrink-0 !ml-2">
						<icon-info />
					</icon-base>
				</Tooltip>
				<Badge class="ml-auto" pill v-if="!collapseOpen.countryBlock"
					:type="features.countryBlock.enabled ? 'enabled' : 'main'">{{ features.countryBlock.enabled ? '• Enabled' :
						'Disabled' }}</Badge>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2">
					<p class="text-xs text-surface-8">Player will not be accessible in selected countries</p>
					<Tooltip wrapper-class="block" :active="!permissions?.countryBlock">
						<FormSwitch class="!block" v-model="features.countryBlock.enabled"
							:disabled="featureProcessing.countryBlock || !permissions?.countryBlock"
							@change="toggleFeature('countryBlock')" />
						<template #content>
							Please <a class="underline" target="_blank" href="/app/subscribe">upgrade</a> subscription to use this
							feature.
						</template>
					</Tooltip>
				</div>
				<div class="col-span-2 flex items-center gap-x-2 !mt-1" v-if="features.countryBlock.enabled">
					<template v-if="countryListLoaded">
						<multiselect v-model="countryBlockSelection" :options="countryList" :multiple="true" :taggable="true"
							tag-placeholder="Unsupported region" placeholder="Search countries..." label="name" track-by="code"
							class="min-h-[2rem]" @select="enableSave('countryBlock')" @remove="enableSave('countryBlock')" />
						<FormButton class="shrink-0" :disabled="isDisabled('countryBlock')" @click="saveGeoBlocking">
							{{ featureProcessing.countryBlock ? 'Saving' : 'Save' }}
						</FormButton>
					</template>
					<template v-else>
						<Spinner />
					</template>
				</div>
			</template>
		</Collapse>
		<!-- Domain Whitelist -->
		<Collapse class="!mb-4" v-model:is-open="collapseOpen.domainWhitelists">
			<template #header>
				<h4 class="text-tiny font-medium">Whitelist Domains</h4>
				<Tooltip wrapper-class="block" content="Restrict this player to specific domains only"
					v-show="!collapseOpen.domainWhitelists">
					<icon-base class="shrink-0 !ml-2">
						<icon-info />
					</icon-base>
				</Tooltip>
				<Badge class="ml-auto" pill v-if="!collapseOpen.domainWhitelists"
					:type="features.domainWhitelists.enabled ? 'enabled' : 'main'">{{ features.domainWhitelists.enabled ? '• Enabled' : 'Disabled' }}</Badge>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2">
					<p class="text-xs text-surface-8">Restrict this player to specific domains only</p>
					<Tooltip wrapper-class="block" :active="!permissions?.domainWhitelists">
						<FormSwitch class="!block" v-model="features.domainWhitelists.enabled"
							:disabled="featureProcessing.domainWhitelists || !permissions?.domainWhitelists"
							@change="toggleFeature('domainWhitelists')" />
						<template #content>
							Please <a class="underline" target="_blank" href="/app/subscribe">upgrade</a> subscription to use this
							feature.
						</template>
					</Tooltip>
				</div>
				<div class="col-span-2 items-center gap-x-2 !mt-1" v-if="features.domainWhitelists.enabled">
					<template v-if="whitelistedDomainsLoaded">
						<div class="flex items-start gap-x-2">
						<FormText v-model="whitelistedDomains" custom-classes="!bg-surface-4 !h-24"
							placeholder="e.g., yoursite.com" @input="enableSave('domainWhitelists')" />
							<FormButton class="shrink-0" :disabled="isDisabled('domainWhitelists')" @click="saveWhitelistedDomains">
								{{ featureProcessing.domainWhitelists ? 'Saving' : 'Save' }}
							</FormButton>
						</div>
						<p class="mt-3 text-xs text-surface-8">
							Whitelisted Domains (one domain per line)
						</p>
						<p class="text-xs text-surface-8 !mb-2">
							Example:
						</p>
						<div class="flex flex-col gap-1 text-error text-xs">
							<code class="text-error">https://example.com</code>
							<code class="text-error">https://mysite.com</code>
							<code class="text-error">https://analytics-site.net</code>
						</div>
					</template>
					<template v-else>
						<Spinner />
					</template>
				</div>
			</template>
		</Collapse>
		<!-- Social Sharing -->
		<Collapse class="!mb-4" v-model:is-open="collapseOpen.socialSharing">
			<template #header>
				<h4 class="text-tiny font-medium">Social Sharing</h4>
				<Tooltip wrapper-class="block" content="Enable sharing buttons on your player"
					v-show="!collapseOpen.socialSharing">
					<icon-base class="shrink-0 !ml-2">
						<icon-info />
					</icon-base>
				</Tooltip>
				<Badge class="ml-auto" pill v-if="!collapseOpen.socialSharing"
					:type="features.socialSharingEnabled.status ? 'enabled' : 'main'">{{ features.socialSharingEnabled.status ? '• Enabled' : 'Disabled' }}</Badge>
			</template>
			<template #content>
				<div class="flex items-center justify-between gap-x-2 -mt-4 !mb-2">
					<p class="text-xs text-surface-8">Enable sharing buttons on your player</p>
					<FormSwitch class="!block" v-model="features.socialSharingEnabled.status" :disabled="featureProcessing.socialSharingEnabled"
						@click="onSocialSharingToggle">
					</FormSwitch>
				</div>

				<ul class="grid grid-cols-1 gap-2 !mb-1 sm:grid-cols-2">
					<li>
						<FormSwitch size="sm" :disabled="!features.socialSharingEnabled.status || featureProcessing.shareFacebook"
							v-model="features.shareFacebook.status" label="Facebook" @click="toggleSocialFeature('shareFacebook')" />
					</li>
					<li>
						<FormSwitch size="sm" :disabled="!features.socialSharingEnabled.status || featureProcessing.shareTwitter"
							v-model="features.shareTwitter.status" label="X (Twitter)" @click="toggleSocialFeature('shareTwitter')" />
					</li>
					<li>
						<FormSwitch size="sm" :disabled="!features.socialSharingEnabled.status || featureProcessing.shareLinkedIn"
							v-model="features.shareLinkedIn.status" label="LinkedIn" @click="toggleSocialFeature('shareLinkedIn')" />
					</li>
					<li>
						<FormSwitch size="sm" :disabled="!features.socialSharingEnabled.status || featureProcessing.shareReddit"
							v-model="features.shareReddit.status" label="Reddit" @click="toggleSocialFeature('shareReddit')" />
					</li>
					<li>
						<FormSwitch size="sm" :disabled="!features.socialSharingEnabled.status || featureProcessing.shareEmail"
							v-model="features.shareEmail.status" label="Email" @click="toggleSocialFeature('shareEmail')" />
					</li>
					<li>
						<FormSwitch size="sm" :disabled="!features.socialSharingEnabled.status || featureProcessing.shareWhatsApp"
							v-model="features.shareWhatsApp.status" label="WhatsApp" @click="toggleSocialFeature('shareWhatsApp')" />
					</li>
				</ul>
			</template>
		</Collapse>
	</aside>

</template>
